"use client";

import { motion, type Variants } from "framer-motion";
import {
  Icon20PlaceOutline,
  Icon20SparkleOutline,
  Icon28LikeOutline,
  Icon24Cancel,
  Icon20ArrowRightOutline,
  Icon20CalendarOutline,
  Icon20FireAltOutline,
  Icon20Stars,
  Icon20MoonOutline,
  Icon20CupOutline,
  Icon20MusicOutline,
  Icon20BrushOutline,
  Icon20PlaneOutline,
} from "@linqx/icons/web";
import { Language } from "@linqx/i18n";
import { cn } from "@/lib/utils";
import type { ScreenKey } from "./flow-data";

/* -------------------------------------------------------------------------- */
/*  Shared motion + primitives                                                */
/* -------------------------------------------------------------------------- */

/** Container that staggers its children — each screen replays this on mount. */
const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.08 } },
};

/** A single element popping into place. */
const pop: Variants = {
  hidden: { opacity: 0, y: 12, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 320, damping: 24 },
  },
};

function Avatar({
  label,
  gradient,
  className,
}: {
  label: string;
  gradient: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-full font-semibold text-white shadow-sm bg-gradient-to-br",
        gradient,
        className,
      )}
    >
      {label}
    </div>
  );
}

function ScreenShell({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="show"
      exit={{ opacity: 0 }}
      className="flex h-full w-full flex-col"
    >
      {children}
    </motion.div>
  );
}

function Header({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <motion.div variants={pop} className="px-4 pt-3">
      <h4 className="font-heading text-lg font-bold leading-tight text-brand-900">
        {title}
      </h4>
      {subtitle ? (
        <p className="text-[11px] text-brand-500">{subtitle}</p>
      ) : null}
    </motion.div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Feature 1 — Discover                                                       */
/* -------------------------------------------------------------------------- */

const VIBES = [
  {
    labelKey: "landing_screen_vibe_adventure",
    icon: Icon20PlaneOutline,
    on: true,
  },
  {
    labelKey: "landing_screen_vibe_nightlife",
    icon: Icon20MoonOutline,
    on: true,
  },
  { labelKey: "landing_screen_vibe_coffee", icon: Icon20CupOutline, on: true },
  {
    labelKey: "landing_screen_vibe_live_music",
    icon: Icon20MusicOutline,
    on: true,
  },
  { labelKey: "landing_screen_vibe_art", icon: Icon20BrushOutline, on: false },
  { labelKey: "landing_screen_vibe_foodie", icon: Icon20CupOutline, on: true },
];

function ScreenVibes() {
  return (
    <ScreenShell>
      <Header
        title={Language.getKey("landing_screen_vibes_title")}
        subtitle={Language.getKey("landing_screen_vibes_subtitle")}
      />
      <div className="grid flex-1 grid-cols-2 content-start gap-2 px-4 pt-4">
        {VIBES.map((v) => (
          <motion.div
            key={v.labelKey}
            variants={pop}
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold",
              v.on
                ? "border-transparent bg-gradient-to-r from-brand-900 to-brand-600 text-white shadow-sm"
                : "border-brand-200 bg-white text-brand-500",
            )}
          >
            <v.icon className="h-3.5 w-3.5 shrink-0" />
            {Language.getKey(v.labelKey)}
          </motion.div>
        ))}
      </div>
      <motion.div variants={pop} className="p-4">
        <div className="btn-brand flex h-11 items-center justify-center gap-2 rounded-full text-sm font-semibold">
          {Language.getKey("landing_screen_vibes_continue")}
          <Icon20ArrowRightOutline className="h-4 w-4" />
        </div>
      </motion.div>
    </ScreenShell>
  );
}

function ScreenNearby() {
  const people = ["A", "M", "J", "K"];
  return (
    <ScreenShell>
      <Header
        title={Language.getKey("landing_screen_nearby_title")}
        subtitle={Language.getKey("landing_screen_nearby_subtitle")}
      />
      <motion.div
        variants={pop}
        className="relative mx-4 mt-4 h-40 overflow-hidden rounded-2xl bg-gradient-to-br from-brand-100 to-brand-200"
      >
        <div className="absolute inset-0 opacity-40 [background-image:linear-gradient(#C4797933_1px,transparent_1px),linear-gradient(90deg,#C4797933_1px,transparent_1px)] [background-size:22px_22px]" />
        {[
          { t: "20%", l: "25%" },
          { t: "55%", l: "60%" },
          { t: "35%", l: "75%" },
          { t: "68%", l: "30%" },
        ].map((p, i) => (
          <div
            key={i}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ top: p.t, left: p.l }}
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-900 text-[10px] font-bold text-white shadow-md ring-2 ring-white">
              {people[i]}
            </div>
          </div>
        ))}
        <div className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-brand-500/40">
          <div className="absolute inset-0 animate-ping rounded-full bg-brand-500/20" />
        </div>
      </motion.div>
      <motion.div
        variants={pop}
        className="mx-4 mt-4 flex items-center gap-3 rounded-2xl border border-brand-100 bg-brand-50 p-3"
      >
        <div className="flex -space-x-2">
          {people.map((p, i) => (
            <Avatar
              key={i}
              label={p}
              gradient="from-brand-400 to-brand-800"
              className="h-8 w-8 text-[10px] ring-2 ring-white"
            />
          ))}
        </div>
        <div className="leading-tight">
          <div className="text-sm font-bold text-brand-900">
            {Language.replaceVarsString(
              Language.getKey("landing_screen_nearby_count"),
              { count: "12" },
            )}
          </div>
          <div className="text-[11px] text-brand-500">
            {Language.replaceVarsString(
              Language.getKey("landing_screen_nearby_radius"),
              { distance: "5 km" },
            )}
          </div>
        </div>
        <Icon20ArrowRightOutline className="ml-auto h-4 w-4 text-brand-400" />
      </motion.div>
    </ScreenShell>
  );
}

function ScreenProfile() {
  return (
    <ScreenShell>
      <div className="flex flex-1 flex-col items-center justify-center px-5 text-center">
        <motion.div variants={pop} className="relative">
          <Avatar
            label="Y"
            gradient="from-brand-400 via-brand-500 to-brand-900"
            className="h-24 w-24 text-3xl"
          />
          <div className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-brand-900 text-white ring-4 ring-white">
            <Icon20SparkleOutline className="h-4 w-4" />
          </div>
        </motion.div>
        <motion.h4
          variants={pop}
          className="mt-4 font-heading text-xl font-bold text-brand-900"
        >
          {Language.replaceVarsString(
            Language.getKey("landing_screen_profile_name"),
            { age: "27" },
          )}
        </motion.h4>
        <motion.div
          variants={pop}
          className="mt-2 flex flex-wrap justify-center gap-1.5"
        >
          {[
            "landing_screen_profile_tag_adventure",
            "landing_screen_profile_tag_coffee",
            "landing_screen_profile_tag_live_music",
          ].map((t) => (
            <span
              key={t}
              className="rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-semibold text-brand-700"
            >
              {Language.getKey(t)}
            </span>
          ))}
        </motion.div>
        <motion.div
          variants={pop}
          className="mt-5 flex items-center gap-2 rounded-full bg-brand-900/5 px-3 py-1.5 text-xs font-semibold text-brand-800"
        >
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-brand-900 text-[9px] text-white">
            ✓
          </span>
          {Language.getKey("landing_screen_profile_ready")}
        </motion.div>
      </div>
    </ScreenShell>
  );
}

/* -------------------------------------------------------------------------- */
/*  Feature 2 — Connect                                                        */
/* -------------------------------------------------------------------------- */

function ScreenSwipe() {
  return (
    <ScreenShell>
      <motion.div
        variants={{
          hidden: { opacity: 0, x: 26, rotate: 7, scale: 0.95 },
          show: {
            opacity: 1,
            x: 0,
            rotate: 0,
            scale: 1,
            transition: { type: "spring", stiffness: 260, damping: 22 },
          },
        }}
        className="relative m-3 flex-1 overflow-hidden rounded-3xl bg-gradient-to-br from-brand-300 via-brand-500 to-brand-900 shadow-lg"
      >
        <div className="absolute -right-8 -top-10 h-40 w-40 rounded-full bg-white/25 blur-2xl" />
        <div className="absolute bottom-24 left-6 h-24 w-24 rounded-full bg-brand-950/30 blur-2xl" />

        <motion.div
          variants={{
            hidden: { opacity: 0, scale: 1.4 },
            show: {
              opacity: 1,
              scale: 1,
              transition: {
                delay: 0.35,
                type: "spring",
                stiffness: 300,
                damping: 14,
              },
            },
          }}
          className="absolute left-5 top-5 -rotate-12 rounded-xl border-2 border-white/90 px-3 py-1 text-sm font-extrabold tracking-widest text-white"
        >
          {Language.getKey("landing_deck_stamp_link")}
        </motion.div>

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-4">
          <div className="font-heading text-2xl font-bold text-white">
            {Language.replaceVarsString(
              Language.getKey("landing_screen_swipe_name"),
              { name: "Maya", age: "26" },
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-1 text-xs text-white/85">
            <Icon20PlaceOutline className="h-3.5 w-3.5" />
            {Language.getKey("landing_screen_swipe_distance")}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[
              "landing_screen_swipe_tag_coffee",
              "landing_screen_swipe_tag_live_music",
              "landing_screen_swipe_tag_art",
            ].map((t) => (
              <span
                key={t}
                className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm"
              >
                {Language.getKey(t)}
              </span>
            ))}
          </div>
        </div>
      </motion.div>

      <motion.div
        variants={pop}
        className="flex items-center justify-center gap-5 pb-4"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-200 bg-white text-brand-400 shadow-sm">
          <Icon24Cancel className="h-5 w-5" />
        </div>
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-brand-900 to-brand-500 text-white shadow-lg shadow-brand-500/40">
          <Icon28LikeOutline className="h-6 w-6" />
        </div>
      </motion.div>
    </ScreenShell>
  );
}

const SPOTS = [
  {
    nameKey: "landing_screen_spot_coffee_name",
    metaKey: "landing_screen_spot_coffee_meta",
    on: true,
    icon: Icon20CupOutline,
  },
  {
    nameKey: "landing_screen_spot_bar_name",
    metaKey: "landing_screen_spot_bar_meta",
    on: false,
    icon: Icon20MoonOutline,
  },
  {
    nameKey: "landing_screen_spot_park_name",
    metaKey: "landing_screen_spot_park_meta",
    on: false,
    icon: Icon20PlaneOutline,
  },
];

function ScreenMeet() {
  return (
    <ScreenShell>
      <Header
        title={Language.getKey("landing_screen_meet_title")}
        subtitle={Language.getKey("landing_screen_meet_subtitle")}
      />
      <div className="flex-1 space-y-2 px-4 pt-3">
        {SPOTS.map((s) => (
          <motion.div
            key={s.nameKey}
            variants={pop}
            className={cn(
              "flex items-center gap-3 rounded-2xl border p-2.5",
              s.on
                ? "border-brand-500 bg-brand-50 ring-1 ring-brand-500"
                : "border-brand-100 bg-white",
            )}
          >
            <div
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-xl",
                s.on
                  ? "bg-gradient-to-br from-brand-900 to-brand-500 text-white"
                  : "bg-brand-50 text-brand-500",
              )}
            >
              <s.icon className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold text-brand-900">
                {Language.getKey(s.nameKey)}
              </div>
              <div className="text-[11px] text-brand-500">
                {Language.getKey(s.metaKey)}
              </div>
            </div>
            {s.on ? (
              <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-brand-900 text-[10px] text-white">
                ✓
              </span>
            ) : null}
          </motion.div>
        ))}
      </div>
      <motion.div variants={pop} className="p-4">
        <div className="flex h-11 items-center justify-center rounded-full border border-brand-300 text-sm font-semibold text-brand-700">
          {Language.getKey("landing_screen_meet_suggest")}
        </div>
      </motion.div>
    </ScreenShell>
  );
}

function ScreenMatch() {
  return (
    <ScreenShell>
      <div className="relative flex flex-1 flex-col items-center justify-center overflow-hidden px-6 text-center">
        {[
          { t: "18%", l: "20%", d: 0 },
          { t: "26%", l: "78%", d: 0.15 },
          { t: "70%", l: "16%", d: 0.3 },
          { t: "76%", l: "82%", d: 0.1 },
          { t: "12%", l: "55%", d: 0.25 },
        ].map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 0.6], scale: [0, 1.2, 1], rotate: 90 }}
            transition={{ delay: 0.3 + s.d, duration: 0.8 }}
            className="absolute text-brand-400"
            style={{ top: s.t, left: s.l }}
          >
            <Icon20SparkleOutline className="h-4 w-4" />
          </motion.div>
        ))}

        <motion.div
          variants={{
            hidden: { scale: 0, rotate: -40 },
            show: {
              scale: 1,
              rotate: 0,
              transition: { type: "spring", stiffness: 220, damping: 14 },
            },
          }}
          className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-brand-900 to-brand-500 text-white shadow-xl shadow-brand-500/40"
        >
          <Icon20SparkleOutline className="h-9 w-9" />
        </motion.div>

        <motion.h4
          variants={pop}
          className="mt-5 font-heading text-2xl font-extrabold text-brand-900"
        >
          {Language.getKey("landing_screen_match_title")}
        </motion.h4>
        <motion.p
          variants={pop}
          className="mt-1 text-sm font-semibold text-brand-600"
        >
          {Language.replaceVarsString(
            Language.getKey("landing_screen_match_pair"),
            { name: "Maya" },
          )}
        </motion.p>
        <motion.p variants={pop} className="mt-2 text-xs text-brand-400">
          {Language.getKey("landing_screen_match_met_irl")}
        </motion.p>

        <motion.div variants={pop} className="mt-5 flex -space-x-3">
          <Avatar
            label="Y"
            gradient="from-brand-400 to-brand-800"
            className="h-12 w-12 ring-2 ring-white"
          />
          <Avatar
            label="M"
            gradient="from-brand-600 to-brand-950"
            className="h-12 w-12 ring-2 ring-white"
          />
        </motion.div>
      </div>
    </ScreenShell>
  );
}

function ScreenChat() {
  return (
    <ScreenShell>
      <motion.div
        variants={pop}
        className="flex items-center gap-2.5 border-b border-brand-100 px-4 py-3"
      >
        <div className="relative">
          <Avatar
            label="M"
            gradient="from-brand-500 to-brand-900"
            className="h-9 w-9 text-xs"
          />
          <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white" />
        </div>
        <div className="leading-tight">
          <div className="text-sm font-bold text-brand-900">Maya</div>
          <div className="text-[10px] font-semibold text-brand-500">
            {Language.getKey("landing_screen_chat_contact_shared")}
          </div>
        </div>
      </motion.div>

      <div className="flex-1 space-y-2 px-4 py-4">
        <motion.div
          variants={pop}
          className="max-w-[78%] rounded-2xl rounded-tl-md bg-brand-50 px-3 py-2 text-xs text-brand-900"
        >
          {Language.getKey("landing_screen_chat_msg_1")}
        </motion.div>
        <motion.div
          variants={pop}
          className="ml-auto max-w-[78%] rounded-2xl rounded-tr-md bg-gradient-to-br from-brand-900 to-brand-600 px-3 py-2 text-xs text-white"
        >
          {Language.getKey("landing_screen_chat_msg_2")}
        </motion.div>
        <motion.div
          variants={pop}
          className="max-w-[78%] rounded-2xl rounded-tl-md bg-brand-50 px-3 py-2 text-xs text-brand-900"
        >
          {Language.getKey("landing_screen_chat_msg_3")}
        </motion.div>
      </div>

      <motion.div variants={pop} className="px-4 pb-4">
        <div className="flex items-center gap-2 rounded-full border border-brand-200 bg-white px-4 py-2">
          <span className="text-xs text-brand-400">
            {Language.getKey("landing_screen_chat_placeholder")}
          </span>
          <div className="ml-auto flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-brand-900 to-brand-500 text-white">
            <Icon20ArrowRightOutline className="h-3.5 w-3.5" />
          </div>
        </div>
      </motion.div>
    </ScreenShell>
  );
}

/* -------------------------------------------------------------------------- */
/*  Feature 3 — Live                                                           */
/* -------------------------------------------------------------------------- */

const EVENTS = [
  {
    nameKey: "landing_screen_event_rooftop_name",
    metaKey: "landing_screen_event_rooftop_meta",
    tagKey: "landing_screen_event_rooftop_tag",
    going: true,
  },
  {
    nameKey: "landing_screen_event_vinyl_name",
    metaKey: "landing_screen_event_vinyl_meta",
    tagKey: "landing_screen_event_vinyl_tag",
    going: false,
  },
];

function ScreenEvents() {
  return (
    <ScreenShell>
      <Header
        title={Language.getKey("landing_screen_events_title")}
        subtitle={Language.getKey("landing_screen_events_subtitle")}
      />
      <div className="flex-1 space-y-3 px-4 pt-3">
        {EVENTS.map((e, i) => (
          <motion.div
            key={e.nameKey}
            variants={pop}
            className="overflow-hidden rounded-2xl border border-brand-100 bg-white shadow-sm"
          >
            <div
              className={cn(
                "relative h-20",
                i === 0
                  ? "bg-gradient-to-br from-brand-500 to-brand-900"
                  : "bg-gradient-to-br from-brand-400 to-brand-700",
              )}
            >
              <div className="absolute -right-4 -top-4 h-16 w-16 rounded-full bg-white/20 blur-xl" />
              <div className="absolute bottom-2 left-3 flex items-center gap-1 text-[10px] font-semibold text-white/90">
                <Icon20CalendarOutline className="h-3.5 w-3.5" />
                {Language.getKey(e.metaKey)}
              </div>
            </div>
            <div className="flex items-center gap-2 p-3">
              <div className="leading-tight">
                <div className="text-sm font-bold text-brand-900">
                  {Language.getKey(e.nameKey)}
                </div>
                <div className="text-[10px] text-brand-500">
                  {Language.getKey("landing_screen_event_location")}
                </div>
              </div>
              <span
                className={cn(
                  "ml-auto rounded-full px-2.5 py-1 text-[10px] font-bold",
                  e.going
                    ? "bg-brand-900 text-white"
                    : "bg-brand-50 text-brand-600",
                )}
              >
                {Language.getKey(e.tagKey)}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </ScreenShell>
  );
}

function ScreenCheckIn() {
  return (
    <ScreenShell>
      <Header
        title={Language.getKey("landing_screen_checkin_title")}
        subtitle={Language.getKey("landing_screen_checkin_subtitle")}
      />
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-5">
        <motion.div
          variants={pop}
          className="grid h-24 w-24 grid-cols-5 gap-0.5 rounded-2xl border border-brand-100 bg-white p-2.5 shadow-sm"
        >
          {Array.from({ length: 25 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "rounded-[2px]",
                (i * 7 + 3) % 3 === 0 ? "bg-brand-900" : "bg-transparent",
              )}
            />
          ))}
        </motion.div>
        <motion.p
          variants={pop}
          className="text-[11px] font-semibold text-brand-500"
        >
          {Language.getKey("landing_screen_checkin_scan")}
        </motion.p>
        <motion.div
          variants={pop}
          className="btn-brand flex h-12 w-full items-center justify-center gap-2 rounded-full text-sm font-bold"
        >
          <Icon20PlaceOutline className="h-4 w-4" />
          {Language.getKey("landing_screen_checkin_button")}
          <span className="rounded-full bg-white/20 px-2 py-0.5 text-[11px]">
            {Language.getKey("landing_screen_checkin_points")}
          </span>
        </motion.div>
        <motion.p variants={pop} className="text-[11px] text-brand-400">
          {Language.getKey("landing_screen_checkin_countdown")}
        </motion.p>
      </div>
    </ScreenShell>
  );
}

interface Leader {
  rank: number;
  /** Literal demo name (kept across locales) — used when `nameKey` is absent. */
  name?: string;
  /** i18n key for the current user's row. */
  nameKey?: string;
  cp: string;
  you: boolean;
}

const LEADERS: Leader[] = [
  { rank: 1, name: "Maya", cp: "1,240", you: false },
  { rank: 2, nameKey: "landing_screen_leaderboard_you", cp: "980", you: true },
  { rank: 3, name: "Sam", cp: "870", you: false },
  { rank: 4, name: "Alex", cp: "640", you: false },
];

function ScreenLeaderboard() {
  return (
    <ScreenShell>
      <Header
        title={Language.getKey("landing_screen_leaderboard_title")}
        subtitle={Language.getKey("landing_screen_leaderboard_subtitle")}
      />
      <div className="flex-1 space-y-2 px-4 pt-3">
        {LEADERS.map((l) => {
          const name = l.nameKey ? Language.getKey(l.nameKey) : l.name!;
          return (
            <motion.div
              key={l.rank}
              variants={pop}
              className={cn(
                "flex items-center gap-3 rounded-2xl border p-2.5",
                l.you
                  ? "border-brand-500 bg-brand-50 ring-1 ring-brand-500"
                  : "border-brand-100 bg-white",
              )}
            >
              <span
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold",
                  l.rank === 1
                    ? "bg-gradient-to-br from-brand-900 to-brand-500 text-white"
                    : "bg-brand-50 text-brand-600",
                )}
              >
                {l.rank}
              </span>
              <Avatar
                label={name[0]}
                gradient="from-brand-400 to-brand-800"
                className="h-8 w-8 text-[11px]"
              />
              <span className="text-sm font-bold text-brand-900">{name}</span>
              <span className="ml-auto flex items-center gap-1 text-xs font-bold text-brand-600">
                <Icon20Stars className="h-3.5 w-3.5" />
                {l.cp}
              </span>
            </motion.div>
          );
        })}
      </div>
      <motion.div variants={pop} className="px-4 pb-4 pt-1">
        <div className="flex items-center justify-center gap-1.5 rounded-full bg-brand-900/5 py-2 text-xs font-bold text-brand-800">
          <Icon20FireAltOutline className="h-4 w-4 text-brand-500" />
          {Language.getKey("landing_screen_leaderboard_footer")}
        </div>
      </motion.div>
    </ScreenShell>
  );
}

/* -------------------------------------------------------------------------- */
/*  Switch                                                                     */
/* -------------------------------------------------------------------------- */

const SCREENS: Record<ScreenKey, () => React.ReactElement> = {
  vibes: ScreenVibes,
  nearby: ScreenNearby,
  profile: ScreenProfile,
  swipe: ScreenSwipe,
  meet: ScreenMeet,
  match: ScreenMatch,
  chat: ScreenChat,
  events: ScreenEvents,
  checkin: ScreenCheckIn,
  leaderboard: ScreenLeaderboard,
};

/** Renders the in-app screen for a given action. */
export function PhoneScreen({ screen }: { screen: ScreenKey }) {
  const Component = SCREENS[screen];
  return <Component />;
}
